import { useFormikContext } from 'formik';
import { ChangeEvent } from 'react';

import { IconNames } from '@shipengine/giger-theme';
import { Button, ButtonSize, Icon, IconSize, Input, Next, Typography } from '@shipengine/giger';
import { useCdnUrl } from '@packlink/provider';
import { useTranslation } from '@packlink/translation-provider';
import { FormField } from '@shipengine/formik-giger';
import { BrandedEmailTemplateParams, EmailType } from '@packlink/packlink-sdk';
import { InfoCard } from '../InfoCard/InfoCard';
import { TemplateFooter } from '../TemplateFooter/TemplateFooter';
import { AmplitudeEventsBulkBrandedEmails } from '@BrandedEmails/metrics/amplitude';
import { LanguageTabOptions } from '@BrandedEmails/BrandedEmailEdition';
import { TextEditor } from '../TextEditor/TextEditor';
import { getViewAmplitudePayload, useAmplitude } from '@common/hooks';
import {
    getInfoCaptionStyles,
    getEmailTemplateSectionStyles,
    getInfoCardsWrapperStyles,
    getCarrierLogoStyles,
    getJourneyLinkStyles,
    getInfoNumberStyles,
    getEmailBodyStyles,
    getEmailBodyHeadingStyles,
    getEmailFormSubmitBtnStyles,
    getMainStyles,
    getCompanyLogoWrapperStyles,
    getNonEditableContentStyles,
    getPlaceholderLogoWrapperStyles,
} from './EmailTemplateEditionStyles';
import { emailTypesKeys } from '../EmailTemplatesList/constants';
import { ImagePreview } from '../ImagePreview/ImagePreview';

export interface EmailTemplateProps {
    companyLogoUrl?: string;
    emailType: EmailType;
    isFormTouched?: boolean;
    deliveryAddress: string;
    deliveryDateNumber: number;
    deliveryCarrierName?: string;
    deliveryCarrierLogo?: string;
    selectedLanguage?: LanguageTabOptions;
}

export function EmailTemplateEdition({
    companyLogoUrl,
    emailType,
    isFormTouched,
    deliveryAddress,
    deliveryDateNumber,
    deliveryCarrierName = 'UPS',
    deliveryCarrierLogo = 'ups-square',
    selectedLanguage,
}: EmailTemplateProps): JSX.Element {
    const { t: tSelectedLanguage } = useTranslation(undefined, { lng: selectedLanguage?.value });
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();
    const MAX_CHARACTER_LENGTH = 500;
    const { sendAmplitudeClickEvent } = useAmplitude();
    const cdnUrl = useCdnUrl();
    const {
        setFieldError,
        errors,
        values,
        initialValues,
        isSubmitting,
        handleChange,
        handleBlur,
        setFieldTouched,
        setFieldValue,
    } = useFormikContext<BrandedEmailTemplateParams>();

    const handleChangeBody = async (value: string, rootElement: HTMLElement | null) => {
        await setFieldTouched('body');
        setFieldValue('body', value, false);

        if ((rootElement?.innerText?.length ?? 0) > MAX_CHARACTER_LENGTH) {
            setFieldError(
                'body',
                t('form.error.max', {
                    field: t('form.label.branded-email-body'),
                    number: MAX_CHARACTER_LENGTH,
                }),
            );
        }
    };

    const handleSubjectChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setFieldTouched('subject');
        handleChange(e);
    };

    return (
        <main css={getMainStyles}>
            <Button
                size={ButtonSize.SMALL}
                css={getEmailFormSubmitBtnStyles}
                type="submit"
                isLoading={isSubmitting}
                disabled={!isFormTouched || Object.keys(errors).length > 0 || isSubmitting}
                onClick={() =>
                    sendAmplitudeClickEvent(
                        AmplitudeEventsBulkBrandedEmails.BRANDED_EMAILS_SAVE_BRANDED_EMAIL,
                        getViewAmplitudePayload(locale),
                    )
                }
            >
                {t('branded-emails.header.save')}
            </Button>
            <div css={getEmailTemplateSectionStyles}>
                <FormField name="subject">
                    <Input
                        label={t('form.label.branded-email-subject')}
                        name="subject"
                        value={values.subject}
                        onChange={handleSubjectChange}
                        onBlur={handleBlur}
                    />
                </FormField>

                <section css={getEmailBodyStyles}>
                    {companyLogoUrl ? (
                        <ImagePreview css={getCompanyLogoWrapperStyles} image={companyLogoUrl} />
                    ) : (
                        <div css={getPlaceholderLogoWrapperStyles}>
                            <Typography component="p" variant="body1">
                                {t('branded-emails.template.logo-placeholder')}
                            </Typography>
                        </div>
                    )}

                    <Typography css={getEmailBodyHeadingStyles} component="h3" variant="heading3">
                        {tSelectedLanguage(`branded-emails.template.heading-${emailTypesKeys[emailType]}`)}
                    </Typography>

                    <FormField name="body">
                        <div>
                            <TextEditor initialHTML={initialValues.body} onChange={handleChangeBody} />
                        </div>
                    </FormField>
                    <div css={getNonEditableContentStyles}>
                        <div css={getInfoCardsWrapperStyles}>
                            <InfoCard title="branded-emails.template.info-card-1" selectedLanguage={selectedLanguage}>
                                <img
                                    css={getCarrierLogoStyles}
                                    src={`${cdnUrl}/apps/carrier-logos/${deliveryCarrierLogo}.svg`}
                                    alt={`${deliveryCarrierLogo} logo`}
                                />
                                <Typography component="h4" variant="body1" bold css={getInfoCaptionStyles}>
                                    {deliveryCarrierName}
                                </Typography>
                                <Next.Link href="#" css={getJourneyLinkStyles}>
                                    {tSelectedLanguage('branded-emails.template.parcel-journey')}
                                    <Icon size={IconSize.SIZE_SMALL} name={IconNames.CHEVRON_RIGHT} />
                                </Next.Link>
                            </InfoCard>
                            <InfoCard title="branded-emails.template.info-card-2" selectedLanguage={selectedLanguage}>
                                <Typography component="h4" variant="body1" bold css={getInfoCaptionStyles}>
                                    {tSelectedLanguage('branded-emails.template.date-day')}
                                </Typography>
                                <Typography component="h4" variant="heading1" css={getInfoNumberStyles}>
                                    {deliveryDateNumber}
                                </Typography>
                                <Typography component="h4" variant="body1" bold css={getInfoCaptionStyles}>
                                    {tSelectedLanguage('branded-emails.template.date-month')}
                                </Typography>
                            </InfoCard>
                            <InfoCard title="branded-emails.template.info-card-3" selectedLanguage={selectedLanguage}>
                                <Typography component="h4" variant="body1" bold css={getInfoCaptionStyles}>
                                    {deliveryAddress}
                                </Typography>
                            </InfoCard>
                        </div>
                        <TemplateFooter selectedLanguage={selectedLanguage} />
                    </div>
                </section>
            </div>
        </main>
    );
}
